// webapp/src/lib/sentry.ts
//
// Sentry React init wrapper — kill-switch + beforeSend rate-limit.
// Refs: phase 18 LAUNCH-14
//
// Kill-switch behavior (Gemini Gate 1 2026-05-07 — verified by behavioral test) :
//   When VITE_SENTRY_ENABLED='false', Sentry.init is NEVER called (early
//   return). This avoids the bug GH Sentry 15229 / Supabase 36489 where an
//   `enabled:false` config can let Sentry.flush hang. The kill-switch is
//   verified by `webapp/src/lib/sentry.test.ts` using vi.mock('@sentry/react') —
//   bundle-level grep is unreliable due to tree-shaking / dead-code-elim.
//
// PII scrubbing (T-18-6-03 mitigation, ASVS L1 V14.1) :
//   `event.user.email` → '[redacted]'
//   `event.request.url` query params (token, email, apikey) stripped.

import * as Sentry from '@sentry/react'
import { shouldCaptureEvent, recordCapture } from './sentryRateLimit'
import { feedbackCopy } from './sentryFeedbackCopy'

// Trace propagation target — the Supabase origin (Edge Functions + REST + Auth).
// Adding it to `tracePropagationTargets` makes the browser SDK attach the
// `sentry-trace` / `baggage` headers on requests to Supabase, so a frontend
// action (booking, checkout) links to its Edge Function trace in Sentry
// (distributed tracing FE<->EF). Falls back to same-origin only if unset.
function supabaseOrigin(): string | undefined {
  try {
    return new URL(import.meta.env.VITE_SUPABASE_URL as string).origin
  } catch {
    return undefined
  }
}

export function initSentry(): void {
  // Kill-switch — early return prevents Sentry.init from running at all.
  // (Avoids the Sentry.flush-hang bug documented in GH Sentry 15229 /
  // Supabase 36489 when only `enabled:false` is used.)
  if (import.meta.env.VITE_SENTRY_ENABLED === 'false') {
    // eslint-disable-next-line no-console
    console.info('[sentry] disabled via VITE_SENTRY_ENABLED=false kill-switch')
    return
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN_REACT
  if (!dsn) {
    // No DSN configured — silent no-op (acceptable in dev/preview without Sentry).
    return
  }

  const sbOrigin = supabaseOrigin()
  // Feedback widget copy for the language of the URL being loaded — see the
  // comment on feedbackIntegration below for why this is read outside React.
  const copy = feedbackCopy(window.location.pathname)

  Sentry.init({
    dsn,
    // Release tag — set to the build version (vite define, = package.json
    // version e.g. `v1.20.1`). Same identifier the @sentry/vite-plugin uploads
    // source maps under, so events bind to maps AND you can see which release a
    // bug appeared in / was fixed in.
    release: __SENTRY_RELEASE__,
    environment: import.meta.env.MODE ?? 'production',
    // RGPD — LA SEULE RAISON POUR LAQUELLE CE BLOC EXISTE.
    //
    // En v10, l'option qui gouvernait la collecte de donnees personnelles etait
    // `sendDefaultPii`, et NE PAS la mettre valait « ne rien collecter ». Ce
    // fichier ne la mettait donc pas : le silence etait restrictif.
    //
    // La v11 a supprime `sendDefaultPii` et l'a remplacee par `dataCollection`
    // — dont le silence est PERMISSIF. Les valeurs par defaut, lues dans les
    // types du paquet (`@sentry/core@11`, `types/datacollection.d.ts`) et non
    // dans un billet de blog, sont `userInfo: true`, `cookies: true`,
    // `httpHeaders: { request: true, response: true }` et `httpBodies` avec les
    // quatre sens actives. Autrement dit : monter la dependance SANS ce bloc
    // aurait mis en route la collecte des cookies, des en-tetes et des corps de
    // requete d'un site qui manipule des emails, des adresses de facturation et
    // des paiements — silencieusement, sans qu'aucun test ne rougisse, puisque
    // la suite vitest mocke `@sentry/react` et n'observe donc que les options
    // PASSEES, jamais ce que le SDK en fait.
    //
    // Les quatre lignes ci-dessous ne durcissent rien de nouveau : elles
    // REDISENT le comportement de la v10. Le scrubbing de `beforeSend` plus bas
    // reste la seconde ceinture, il ne remplace pas celle-ci (il redige
    // `user.email`, pas `user.ip_address` ni les cookies).
    //
    // `urlQueryParams: false` va DELIBEREMENT au-dela de la parite v10, et
    // c'est la seule ligne de ce bloc dont ce soit le cas. Raison : le
    // `beforeSend` plus bas ne nettoie la query string que de
    // `event.request.url`. Il ne touche PAS `event.breadcrumbs`, ou le SDK
    // enregistre tout seul les URLs de navigation et de fetch — donc un
    // `?token=...` de lien magique partait deja vers Sentry en v10, par ce
    // chemin-la. La v11 offre l'interrupteur global qui n'existait pas ; le
    // prendre coute une ligne et ferme une fuite reelle. La consequence
    // assumee : les URLs arrivent sans query string, ce dont ni le
    // regroupement d'erreurs ni la lecture d'une stack n'ont besoin.
    //
    // `stackFrameVariables` n'est PAS remis a `false` ici, malgre son defaut
    // permissif : la capture de variables locales demande une integration que
    // `@sentry/browser` ne fournit pas (verifie — aucun `localvariables` dans
    // build/npm/types/integrations/). Ajouter l'option ne ferait qu'ajouter du
    // bruit inerte.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      // `httpBodies` est type `HttpBodyCollectionTarget[]`, jamais un booleen :
      // le tableau vide EST la forme documentee pour tout desactiver.
      httpBodies: [],
      urlQueryParams: false,
    },
    // Distributed tracing — propagate trace headers to the Supabase backend so
    // FE actions link to their Edge Function spans. Same-origin is always traced.
    tracesSampleRate: 0.1,
    tracePropagationTargets: sbOrigin ? ['localhost', /^\//, sbOrigin] : ['localhost', /^\//],
    // Session Replay — record the session leading up to an error to reproduce
    // it. Privacy/RGPD: mask ALL text + ALL inputs and block media so no email,
    // PIN, promo code or payment field is ever captured (only structure/clicks).
    // 0% of normal sessions, 100% of sessions that hit an error.
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
      // User Feedback — a persistent button (autoInject) plus a programmatic
      // open from the error fallback (see ErrorBoundaryFallback). Sentry
      // branding hidden; dark theme to match the app.
      //
      // Copy comes from `sentryFeedbackCopy` and follows the URL language
      // (phase 98, D-04). It used to be twelve hard-coded FRENCH labels shown
      // on every page, the 15 English `/en/` twins included.
      //
      // The language is read OUTSIDE React, here, because initSentry() runs in
      // main.tsx BEFORE <LanguageProvider> mounts — useLanguage() is not
      // reachable. That is legitimate since phase 96 D-03: the language is
      // constant for the lifetime of the document (it only ever changes by a
      // document navigation), so one synchronous read is enough. Same idiom as
      // AuthProvider, for the same reason.
      //
      // ITS LIMIT, stated so nobody rediscovers it as a bug: should the
      // language ever become switchable without a reload, this widget would
      // stay in the load-time language. The way out then is (a) or (b) of
      // 98-RESEARCH §5 — re-init or a language-keyed remount — NOT a patch here.
      Sentry.feedbackIntegration({
        autoInject: true,
        showBranding: false,
        colorScheme: 'dark',
        ...copy,
      }),
    ],
    beforeSend(event) {
      if (!shouldCaptureEvent()) {
        return null // Drop event — rate limit hit.
      }
      recordCapture()

      // PII scrubbing (T-18-6-03 mitigation, ASVS L1 V14.1).
      if (event.user?.email) {
        event.user = { ...event.user, email: '[redacted]' }
      }
      if (event.request?.url) {
        try {
          const url = new URL(event.request.url)
          url.searchParams.delete('token')
          url.searchParams.delete('email')
          url.searchParams.delete('apikey')
          event.request.url = url.toString()
        } catch {
          // URL parse failed — leave as-is.
        }
      }
      return event
    },
  })
}
